// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=48%3A264
import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { Avatar, Box } from '@mui/material';
import type { Interpolation } from '@emotion/serialize';
import type { Theme } from '@emotion/react';
import type { GlobalToken } from 'antd';
import { DataElementId } from '../../../constants/data-element-tracking-ids';
import {
  NAV_CONTEXT_MENU_BUTTON_BASE_STYLE,
  NavContextMenu,
} from './nav-context-menu';
import Typography, {
  useGetTypographyStyles,
  TypographyVariant,
} from '../../../component_library/components/Typography';
import { Link } from '../../../component_library/components/Link';
import {
  Button,
  ButtonVariant,
} from '../../../component_library/components/Button';
import type { IHeaderSectionData } from '../../../dotcms/dotcms-models/dotcms-header';
import { fetchLogout } from '../../../slices/auth';
import type { ITheme } from '../../../component_library/styles/theme';
import { useUserContextMenu } from './hooks';

export type IHeaderDataForUserContextMenu = Pick<
IHeaderSectionData,
'loginButtonLabels' | 'myAccountLinkSign'
>;

interface IProps extends IHeaderDataForUserContextMenu {
  theme: ITheme;
  themeToken: GlobalToken;
  homepagePath: string;
  hideAvatar?: boolean;
  useDarkFontColor?: boolean;
}

const UserContextMenu: React.FC<IProps> = ({
  theme,
  themeToken,
  homepagePath,
  loginButtonLabels,
  myAccountLinkSign,
  hideAvatar,
  useDarkFontColor,
}) => {
  const {
    dispatch,
    history,
    issuerShortName,
    showLoginButton,
    loginUrl,
    showLogoutButton,
    isAccountPageAvailable,
    accountPageUrl,
    avatarUserName,
    displayedUserName,
  } = useUserContextMenu();

  const titleRef = useRef<HTMLDivElement>();

  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenPage = useCallback(
    (pageUrl: string, handleCloseTooltipWithNoTransition: () => void) => {
      handleCloseTooltipWithNoTransition();
      history.push(pageUrl);
    },
    [history],
  );

  const handleLogout = useCallback(
    (handleCloseTooltipWithNoTransition: () => void) => {
      dispatch(fetchLogout({ shortName: issuerShortName }));
      handleOpenPage(homepagePath, handleCloseTooltipWithNoTransition);
    },
    [dispatch, handleOpenPage, homepagePath, issuerShortName],
  );

  const nameStyles: Interpolation<Theme> = useMemo(
    () => ({
      wordBreak: 'break-all',
      fontSize:
        titleRef.current && titleRef.current.clientHeight > 50
          ? '12px'
          : undefined,
    }),
    [],
  );

  const menuButtonTypographyStyles = useGetTypographyStyles(
    TypographyVariant.SMALL_BODY_REGULAR,
    theme,
    themeToken,
    theme.colors.secondary,
  );

  const menuButtonStyles = useMemo(
    () => ({
      outline: 'none',
      ...menuButtonTypographyStyles,
    }),
    [menuButtonTypographyStyles],
  );

  if (showLoginButton) {
    return (
      <Link
        variant={TypographyVariant.SMALL_BODY_BOLD}
        color={theme.colors.grayscale.white}
        hideDecorations
        to={loginUrl}
        dataElementId={DataElementId.HEADER_LINK_LOGIN}
        themeToken={themeToken}
      >
        {loginButtonLabels.loginRoleLabel}
      </Link>
    );
  }

  const titleContent = (
    <Box sx={NAV_CONTEXT_MENU_BUTTON_BASE_STYLE} ref={titleRef}>
      {!hideAvatar && (
        <Avatar
          sx={{
            bgcolor: theme.colors.primary,
            color: theme.colors.grayscale.white,
            WebkitTextFillColor: theme.colors.grayscale.white,
            width: '32px',
            height: '32px',
            fontFamily: 'Barlow',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '12px',
          }}
        >
          {avatarUserName}
        </Avatar>
      )}
      <Typography
        variant={TypographyVariant.SMALL_BODY_BOLD}
        theme={theme}
        additionalStyles={nameStyles}
      >
        {displayedUserName}
      </Typography>
    </Box>
  );

  const renderMenuContent: React.FC<{
    handleCloseTooltipWithNoTransition: () => void;
  }> = ({ handleCloseTooltipWithNoTransition }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
      {isAccountPageAvailable && !!myAccountLinkSign && (
        <Button
          type="button"
          variant={ButtonVariant.FLAT}
          inlineButton
          hideDecorations
          onClick={() =>
            handleOpenPage(accountPageUrl, handleCloseTooltipWithNoTransition)}
          dataElementId={DataElementId.HEADER_LINK_ACCOUNT}
          themeToken={themeToken}
          additionalStyles={menuButtonStyles}
        >
          {myAccountLinkSign}
        </Button>
      )}
      {showLogoutButton && (
        <Button
          type="button"
          variant={ButtonVariant.FLAT}
          inlineButton
          hideDecorations
          onClick={() => handleLogout(handleCloseTooltipWithNoTransition)}
          dataElementId={DataElementId.HEADER_LINK_LOGOUT}
          themeToken={themeToken}
          additionalStyles={menuButtonStyles}
        >
          {loginButtonLabels?.logoutRoleLabel}
        </Button>
      )}
    </Box>
  );

  return (
    <NavContextMenu
      dataElementId={DataElementId.NAV_USER_CONTEXT_MENU}
      useDarkFontColor={useDarkFontColor}
      titleProps={{
        dataElementId: DataElementId.NAV_USER_CONTEXT_MENU,
        content: titleContent,
      }}
      renderTooltipContent={renderMenuContent}
      menuOpen={menuOpen}
      setMenuOpen={setMenuOpen}
      theme={theme}
    />
  );
};

export default UserContextMenu;
